/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useSignInFlowType } from '@/components/SignInFlowTypeContext';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useState,
  useCallback,
} from 'react';

export type Views =
  | 'login'
  | 'password'
  | 'create_account'
  | 'email_otp_code'
  | 'sms_otp_code'
  | 'validate_otp_code'
  | 'validate_otp_code_sms'
  | 'forgot_password'
  | 'forgot_password_sms'
  | 'validate_forgot_password'
  | 'validate_forgot_password_sms'
  | 'keep_current_password'
  | 'auto_sms_otp_code'
  | 'auto_email_otp_code'
  | 'auto_validate_email_otp_code'
  | 'auto_validate_sms_otp_code'
  | 'auto_create_new_password'
  | 'create_new_password';

export type AccountTypes =
  | 'Anonymous'
  | 'Known Anonymous'
  | 'Standalone Online'
  | 'Online + Rewards'
  | 'Auto-Created'
  | 'No Account'
  | 'Standalone Rewards'
  | 'Warranty';

type LoginMethods = 'SMS OTP' | 'Email OTP' | 'Password';

type AccountViewProviderType = {
  view: Views;
  userName: string;
  autoSubmitEmail: boolean;
  maskedEmail: string;
  userMaskedPhoneNumber: string;
  loading: boolean;
  disableSignIn: boolean;
  showHeaderAndFooter: boolean;
  openEmailSent: boolean;
  isModal: boolean;
  loginFlow: AccountTypes;
  rememberMe: boolean;
  showFieldLevelValidation: boolean;
  isResendCode: boolean;
  isPhoneNumber: boolean;
  lastName: string;
  loginMethod: LoginMethods;
  receiveSMS?: boolean;
  hideLoginSnackbar?: boolean;
  setView: Dispatch<SetStateAction<Views>>;
  setUserName: Dispatch<SetStateAction<string>>;
  setAutoSubmitEmail: Dispatch<SetStateAction<boolean>>;
  setUserMaskedPhoneNumber: Dispatch<SetStateAction<string>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setDisableSignIn: Dispatch<SetStateAction<boolean>>;
  setShowHeaderAndFooter: Dispatch<SetStateAction<boolean>>;
  setOpenEmailSent: Dispatch<SetStateAction<boolean>>;
  setIsModal: Dispatch<SetStateAction<boolean>>;
  setLoginFlow: Dispatch<SetStateAction<AccountTypes>>;
  setRememberMe: Dispatch<SetStateAction<boolean>>;
  setShowFieldLevelValidation: Dispatch<SetStateAction<boolean>>;
  setMaskedEmail: Dispatch<SetStateAction<string>>;
  onCloseModal?: (userAuthenticated?: boolean) => Promise<void> | void;
  setIsResendCode: Dispatch<SetStateAction<boolean>>;
  setIsPhoneNumber: Dispatch<SetStateAction<boolean>>;
  setLastName: Dispatch<SetStateAction<string>>;
  setLoginMethod: Dispatch<SetStateAction<LoginMethods>>;
  setReceiveSMS: Dispatch<SetStateAction<boolean | undefined>>;
  reset: (clearLastName?: boolean) => void;
};

export const AccountViewContext = createContext<AccountViewProviderType>(
  {} as AccountViewProviderType,
);

type AccountViewProviderProps = {
  hideLoginSnackbar?: boolean;
  showHeaderAndFooterProp: boolean;
  onCloseModal?: (userAuthenticated?: boolean) => Promise<void> | void;
  children: ReactNode;
};

export function AccountViewProvider({
  hideLoginSnackbar,
  onCloseModal,
  showHeaderAndFooterProp,
  children,
}: AccountViewProviderProps) {
  const { onCloseNotification, resetSignInFlowTypeContextFn } = useSignInFlowType();
  const [view, setView] = useState<Views>('login');
  const [userName, setUserName] = useState('');
  const [autoSubmitEmail, setAutoSubmitEmail] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState<string>('');
  const [userMaskedPhoneNumber, setUserMaskedPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [disableSignIn, setDisableSignIn] = useState(true);
  const [showHeaderAndFooter, setShowHeaderAndFooter] = useState(showHeaderAndFooterProp);
  const [openEmailSent, setOpenEmailSent] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [showFieldLevelValidation, setShowFieldLevelValidation] = useState(false);
  const [loginFlow, setLoginFlow] = useState<AccountTypes>('No Account');
  const [rememberMe, setRememberMe] = useState(true);
  const [isResendCode, setIsResendCode] = useState(false);
  const [isPhoneNumber, setIsPhoneNumber] = useState(false);
  const [lastName, setLastName] = useState('');
  const [loginMethod, setLoginMethod] = useState<LoginMethods>('Password');
  const [receiveSMS, setReceiveSMS] = useState<boolean | undefined>(undefined);

  function reset(clearLastName = true) {
    onCloseNotification();
    setAutoSubmitEmail(false);
    setLoading(false);
    setDisableSignIn(true);
    setIsResendCode(false);
    setShowFieldLevelValidation(false);
    if (clearLastName) {
      setLastName('');
    }
  }

  const onClose = useCallback(
    async (userAuthenticated?: boolean) => {
      try {
        await onCloseModal?.(userAuthenticated);
      } finally {
        resetSignInFlowTypeContextFn();
      }
    },
    [onCloseModal, resetSignInFlowTypeContextFn],
  );

  return (
    <AccountViewContext
      value={{
        view,
        userName,
        autoSubmitEmail,
        maskedEmail,
        userMaskedPhoneNumber,
        loading,
        disableSignIn,
        showHeaderAndFooter,
        openEmailSent,
        isModal,
        loginFlow,
        rememberMe,
        isResendCode,
        isPhoneNumber,
        lastName,
        loginMethod,
        receiveSMS,
        hideLoginSnackbar,
        setView,
        setUserName,
        setAutoSubmitEmail,
        setUserMaskedPhoneNumber,
        setLoading,
        reset,
        setDisableSignIn,
        setShowHeaderAndFooter,
        setOpenEmailSent,
        setIsModal,
        setLoginFlow,
        setRememberMe,
        onCloseModal: onClose,
        setMaskedEmail,
        showFieldLevelValidation,
        setShowFieldLevelValidation,
        setIsResendCode,
        setIsPhoneNumber,
        setLastName,
        setLoginMethod,
        setReceiveSMS,
      }}
    >
      {children}
    </AccountViewContext>
  );
}

export default AccountViewProvider;
